import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./OnboardingMaterials.css";
import { getAPIURL } from "../../api/config";
import { UserContext } from "../../contexts/CurrentUserContext";
import { canAccessOnboardingWelcome } from "../../utils/onboardingAccess";

const presentationFileName = "Welcome.pptx";

const buildPresentationUrl = () => {
  const fromEnv = process.env.REACT_APP_ONBOARDING_PRESENTATION_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim();
  }
  const apiBaseUrl = getAPIURL().replace(/\/+$/, "");
  const encodedFileName = encodeURIComponent(presentationFileName);
  return `${apiBaseUrl}/images/posts/${encodedFileName}`;
};

const presentationUrl = buildPresentationUrl();

function getYouTubeVideoId(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.replace(/^\//, "").split("/")[0];
      return id || "";
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname.startsWith("/embed/")) {
        const id = parsed.pathname.slice("/embed/".length).split("/")[0];
        return id || "";
      }
      return parsed.searchParams.get("v") || "";
    }
  } catch {
    return "";
  }
  return "";
}

/** YouTube watch / youtu.be URLs redirect to pages that cannot be iframed; embed URLs can. */
function toYouTubeEmbedSrc(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.replace(/^www\./, "");
    if (
      (host === "youtube.com" || host === "m.youtube.com") &&
      parsed.pathname.startsWith("/embed/")
    ) {
      return `${parsed.origin}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    return "";
  }
  const id = getYouTubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : "";
}

/** Статичные превью с CDN YouTube, API не нужен. mqdefault — ~320×180, обычно есть у всех роликов. */
function getYouTubeThumbnailSrc(videoId) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg` : "";
}

const presentation = {
  title: "Welcome презентация",
  description:
    "Презентация знакомит с историей компании, структурой, ключевыми проектами и корпоративной культурой.",
  pages: 22,
  type: "PPTX",
  size: "62 MB",
  downloadUrl: presentationUrl,
};

const lessons = [
  {
    id: 1,
    title: "Создание отчета",
    description: "Как создавать отчеты о проделанной работе?",
    duration: "0:33",
    youtubeEmbedUrl: "https://youtu.be/M7U2Vzw2NG0?si=lFTguEXvalpZPsTr",
  },
  {
    id: 2,
    title: "Создание отчета с несколькими проектами",
    description: "Как создать отчеты с несколькими проектами?",
    duration: "0:57",
    youtubeEmbedUrl: "https://youtu.be/IeG7SoYWZk4?si=zq6Kl2SwuzmEUsWZ",
  },
  {
    id: 3,
    title: "Редактирование отчета",
    description: "Как редактировать отчеты?",
    duration: "0:34",
    youtubeEmbedUrl: "https://youtu.be/XVjockgIv3Y?si=Aqz8kYYuK-HAWP9G",
  },
  {
    id: 4,
    title: "Как пользоваться календарем?",
    description: "Как пользоваться календарем?",
    duration: "0:23",
    youtubeEmbedUrl: "https://youtu.be/H4qSl1OzqRA?si=yO3iRjUrJaz7Quo3",
  },
];

function OnboardingMaterials() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [activeLessonId, setActiveLessonId] = useState(lessons[0].id);
  const activeLesson = useMemo(
    () => lessons.find((lesson) => lesson.id === activeLessonId) || lessons[0],
    [activeLessonId],
  );

  const hasOnboardingAccess = canAccessOnboardingWelcome(userData);

  useEffect(() => {
    if (!hasOnboardingAccess) {
      navigate("/", { replace: true });
    }
  }, [hasOnboardingAccess, navigate]);

  const handleLessonCardKeyDown = (event, lessonId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setActiveLessonId(lessonId);
    }
  };

  if (!hasOnboardingAccess) {
    return null;
  }

  return (
    <section className="onboarding-materials">
      <header className="onboarding-materials__header">
        <h1 className="onboarding-materials__title">Материалы для адаптации</h1>
        <p className="onboarding-materials__subtitle">
          Здесь собраны материалы, которые помогут быстро познакомиться с
          компанией, процессами и порталом.
        </p>
      </header>

      <div className="onboarding-materials__roadmap">
        <div className="onboarding-materials__roadmap-main">
          <h2 className="onboarding-materials__roadmap-title">С чего начать</h2>
          <ol className="onboarding-materials__steps">
            <li>
              <span className="onboarding-materials__step-number">1</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Посмотрите welcome-презентацию
                </p>
                <p className="onboarding-materials__step-desc">
                  Узнайте о компании, структуре и основных принципах работы
                </p>
              </div>
            </li>
            <li>
              <span className="onboarding-materials__step-number">2</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Посмотрите вводные видео
                </p>
                <p className="onboarding-materials__step-desc">
                  Короткие ролики помогут освоить портал и процессы
                </p>
              </div>
            </li>
            <li>
              <span className="onboarding-materials__step-number">3</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Перейдите к работе в системе
                </p>
                <p className="onboarding-materials__step-desc">
                  Изучите ключевые разделы портала
                </p>
              </div>
            </li>
          </ol>
        </div>

        <div className="onboarding-materials__roadmap-meta">
          <p>Всего материалов</p>
          <h3>1 презентация + 4 видео</h3>
          <span>Примерно 15-20 минут</span>
        </div>
      </div>

      <h2 className="onboarding-materials__section-title">
        Welcome-презентация
      </h2>
      <article className="onboarding-materials__presentation-card">
        <div className="onboarding-materials__presentation-icon">📄</div>
        <div className="onboarding-materials__presentation-content">
          <h3>{presentation.title}</h3>
          <p>{presentation.description}</p>
          <span>
            {presentation.pages} слайдa • {presentation.type},{" "}
            {presentation.size}
          </span>
        </div>
        <div className="onboarding-materials__presentation-actions">
          <a href={presentation.downloadUrl} download>
            Скачать
          </a>
        </div>
      </article>

      <h2 className="onboarding-materials__section-title">Видеоматериалы</h2>
      <div className="onboarding-materials__video-layout">
        <div className="onboarding-materials__video-player">
          <iframe
            key={activeLesson.id}
            src={toYouTubeEmbedSrc(activeLesson.youtubeEmbedUrl)}
            title={activeLesson.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <div className="onboarding-materials__video-caption">
            Сейчас выбран: {activeLesson.title}
          </div>
        </div>

        <div className="onboarding-materials__video-list">
          {lessons.map((lesson) => {
            const videoId = getYouTubeVideoId(lesson.youtubeEmbedUrl);
            return (
              <article
                key={lesson.id}
                role="button"
                tabIndex={0}
                aria-label={`Выбрать видео: ${lesson.title}`}
                aria-current={
                  lesson.id === activeLesson.id ? "true" : undefined
                }
                className={`onboarding-materials__video-item${
                  lesson.id === activeLesson.id
                    ? " onboarding-materials__video-item--active"
                    : ""
                }`}
                onClick={() => setActiveLessonId(lesson.id)}
                onKeyDown={(event) =>
                  handleLessonCardKeyDown(event, lesson.id)
                }
              >
                <div className="onboarding-materials__video-thumb">
                  {videoId ? (
                    <img
                      className="onboarding-materials__video-thumb-img"
                      src={getYouTubeThumbnailSrc(videoId)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                  <span>{lesson.duration}</span>
                </div>
                <div className="onboarding-materials__video-info">
                  <div className="onboarding-materials__video-row">
                    <strong>{lesson.id}</strong>
                    <h3>{lesson.title}</h3>
                  </div>
                  <p>{lesson.description}</p>
                  <span className="onboarding-materials__video-watch">
                    Смотреть
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default OnboardingMaterials;
